import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { getExpertsAnalytics, exportAnalyticsData } from '../../utils/analyticsApi';

function ExpertsAnalytics() {
  const navigate = useNavigate();

  // Mock data for demonstration
  const metrics = {
    totalConsultations: 342,
    totalRevenue: '$68,400',
    averageRating: '4.8',
    responseTime: '2.3 hrs',
    activeExperts: 12,
    completionRate: '94%'
  };

  const topExperts = [
    { name: 'Sarah Johnson', specialty: 'Music Production', sessions: 45, rating: 4.9, earnings: '$9,000' },
    { name: 'Mike Chen', specialty: 'Audio Engineering', sessions: 38, rating: 4.8, earnings: '$7,600' },
    { name: 'Alex Rivera', specialty: 'Songwriting', sessions: 32, rating: 4.9, earnings: '$6,400' },
    { name: 'Emma Davis', specialty: 'Marketing', sessions: 28, rating: 4.7, earnings: '$5,600' },
    { name: 'Jordan Kim', specialty: 'Performance', sessions: 25, rating: 4.8, earnings: '$5,000' }
  ];

  const categories = [
    { name: 'Music Production', sessions: 128, percentage: 37 },
    { name: 'Audio Engineering', sessions: 89, percentage: 26 },
    { name: 'Songwriting', sessions: 67, percentage: 20 },
    { name: 'Marketing', sessions: 34, percentage: 10 },
    { name: 'Performance', sessions: 24, percentage: 7 }
  ];

  const MetricCard = ({ title, value, subtitle, color }) => (
    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[rgba(255,255,255,0.2)] transition-all">
      <div className="flex items-center gap-2 mb-2">
        <div 
          className="w-[8px] h-[8px] rounded-full"
          style={{ backgroundColor: color }}
        />
        <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
          {title}
        </span>
      </div>
      <div className="text-white text-[18px] font-bold mb-1">{value}</div>
      {subtitle && (
        <div className="text-[rgba(255,255,255,0.5)] text-[10px]">{subtitle}</div>
      )}
    </div>
  );

  const ExpertCard = ({ expert }) => (
    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 hover:border-[rgba(255,255,255,0.2)] transition-all">
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1">
          <div className="text-white text-[12px] font-semibold">{expert.name}</div>
          <div className="text-[rgba(255,255,255,0.7)] text-[10px]">{expert.specialty}</div>
        </div>
        <div className="text-right ml-2">
          <div className="text-[#FF9500] text-[12px] font-bold">{expert.earnings}</div>
          <div className="flex items-center gap-1">
            <span className="text-yellow-400 text-[10px]">★</span>
            <span className="text-[rgba(255,255,255,0.5)] text-[10px]">{expert.rating}</span>
          </div>
        </div>
      </div>
      <div className="text-[rgba(255,255,255,0.5)] text-[10px]">{expert.sessions} sessions completed</div>
    </div>
  );

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between">
            <div className="flex items-center gap-2">
              <div 
                className="w-[24px] h-[24px] rounded-md flex items-center justify-center text-[8px] font-medium text-white"
                style={{ backgroundColor: '#FF9500' }}
              >
                EX
              </div>
              <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Experts Analytics</span>
            </div>
            <div className="flex items-center gap-4">
              <p 
                className='text-[red] cursor-pointer text-[12px]'
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('dizeeToken');
                    const response = await exportAnalyticsData(token, 'experts', 'csv');
                    if (response.status === 200) {
                      alert('Export data prepared! Check your downloads.');
                    }
                  } catch (error) {
                    console.error('Error exporting data:', error);
                    alert('Export failed. Please try again.');
                  }
                }}
              >
                Export data
              </p>
              <div 
                className="text-white cursor-pointer text-[12px] hover:text-gray-400 transition-colors"
                onClick={() => navigate('/analytics/hub')}
              >
                Back
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="w-full px-4 flex-1 overflow-y-auto pb-[100px]">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <MetricCard 
                title="Total Sessions" 
                value={metrics.totalConsultations} 
                subtitle="This month"
                color="#FF9500" 
              />
              <MetricCard 
                title="Total Revenue" 
                value={metrics.totalRevenue} 
                subtitle="This month"
                color="#FF9500" 
              />
              <MetricCard 
                title="Average Rating" 
                value={metrics.averageRating} 
                subtitle="Out of 5.0"
                color="#FF9500" 
              />
              <MetricCard 
                title="Response Time" 
                value={metrics.responseTime} 
                subtitle="Average"
                color="#FF9500" 
              />
            </div>

            {/* Consultation Trend Chart Placeholder */}
            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-[8px] h-[8px] rounded-full bg-[#FF9500]" />
                <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
                  Session Trend
                </span>
              </div>
              <div className="h-[120px] bg-[rgba(255,255,255,0.03)] rounded border border-[rgba(255,255,255,0.1)] flex items-center justify-center">
                <div className="text-center">
                  <div className="text-[#FF9500] text-[12px] font-bold mb-2">CHART</div>
                  <div className="text-[rgba(255,255,255,0.5)] text-[10px]">Consultation Chart</div>
                  <div className="text-[rgba(255,255,255,0.3)] text-[10px]">Interactive chart coming soon</div>
                </div>
              </div>
            </div>

            {/* Expertise Categories */}
            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-[8px] h-[8px] rounded-full bg-[#FF9500]" />
                <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
                  Popular Categories
                </span>
              </div>
              <div className="space-y-2">
                {categories.map((category, index) => (
                  <div key={category.name} className="flex justify-between items-center">
                    <span className="text-white text-[12px]">{category.name}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-[60px] bg-[rgba(255,255,255,0.1)] h-[4px] rounded">
                        <div 
                          className="bg-[#FF9500] h-full rounded"
                          style={{ width: `${category.percentage}%` }}
                        />
                      </div>
                      <span className="text-[rgba(255,255,255,0.7)] text-[10px] w-[30px] text-right">
                        {category.sessions}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Performing Experts */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-[8px] h-[8px] rounded-full bg-[#FF9500]" />
                <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
                  Top Performing Experts
                </span>
              </div>
              <div className="space-y-3">
                {topExperts.map((expert, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="text-[#FF9500] text-[12px] font-bold w-[20px]">
                      #{index + 1}
                    </div>
                    <div className="flex-1">
                      <ExpertCard expert={expert} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-[8px] h-[8px] rounded-full bg-[#FF9500]" />
                <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
                  Performance Overview
                </span>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-green-400 text-[16px] font-bold">{metrics.completionRate}</div>
                  <div className="text-[rgba(255,255,255,0.5)] text-[10px]">Completion Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-[#FF9500] text-[16px] font-bold">{metrics.activeExperts}</div>
                  <div className="text-[rgba(255,255,255,0.5)] text-[10px]">Active Experts</div>
                </div>
                <div className="text-center">
                  <div className="text-yellow-400 text-[16px] font-bold">{metrics.averageRating}</div>
                  <div className="text-[rgba(255,255,255,0.5)] text-[10px]">Avg Rating</div>
                </div>
              </div>
            </div>

            {/* Session Duration Distribution */}
            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-[8px] h-[8px] rounded-full bg-[#FF9500]" />
                <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
                  Session Duration
                </span>
              </div>
              <div className="space-y-2">
                {[
                  { duration: '30 min', count: 156, percentage: 45 },
                  { duration: '60 min', count: 124, percentage: 36 },
                  { duration: '90 min', count: 42, percentage: 12 },
                  { duration: '120+ min', count: 20, percentage: 7 }
                ].map((item, index) => (
                  <div key={item.duration} className="flex justify-between items-center">
                    <span className="text-white text-[12px]">{item.duration}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-[60px] bg-[rgba(255,255,255,0.1)] h-[4px] rounded">
                        <div 
                          className="bg-[#FF9500] h-full rounded"
                          style={{ width: `${item.percentage}%` }}
                        />
                      </div>
                      <span className="text-[rgba(255,255,255,0.7)] text-[10px] w-[30px] text-right">
                        {item.count}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Help Text with Black Overlay */}
          <div className="w-full absolute bottom-0 left-0">
            <div className="bg-black px-4 pt-8 pb-[27px]">
              <div className="text-[12px] text-[rgba(255,255,255,0.5)] leading-[1.5] max-w-full">
                <p>Monitor expert consultation performance and client satisfaction.</p>
                <p>Track session metrics and optimize your expert network.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
}

export default ExpertsAnalytics; 